<script setup lang="ts">
import { computed, useTemplateRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import AppShellLeftMenu from '@/components/layouts/app-shell/app-shell-left-menu.vue';
import { useHideOnScroll } from '@/utils/composables/useHideOnScroll';
import { provideLayoutScrollContainer } from '@/utils/composables/useLayoutScrollContainer';
import { provideLayoutVariant } from '@/utils/composables/useLayoutVariant';

interface Props {
  panelWidth?: number;
  hasPanel?: boolean;
  isResizing?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  panelWidth: 0,
  hasPanel: true,
  isResizing: false,
});

defineEmits<{
  toggle: [];
  dragStart: [event: PointerEvent];
}>();

provideLayoutVariant('next');

const contentRef = useTemplateRef('contentRef');
provideLayoutScrollContainer(contentRef);

const { isHidden: isHeaderHidden, reveal } = useHideOnScroll(contentRef);

const route = useRoute();
watch(() => route.fullPath, reveal);

const effectivePanelWidth = computed(() =>
  props.hasPanel ? props.panelWidth : 0,
);
const isCollapsed = computed(() => effectivePanelWidth.value === 0);
</script>

<template>
  <div
    class="app-shell"
    :class="{ 'is-resizing': props.isResizing }"
    :style="{
      '--layout-header-offset': isHeaderHidden
        ? 'calc(var(--layout-header-height) * -1)'
        : '0px',
      '--layout-left-menu-panel-width': `${effectivePanelWidth}px`,
    }"
  >
    <div class="app-shell__header">
      <slot name="header"></slot>
    </div>
    <div class="app-shell__menu">
      <AppShellLeftMenu
        :panel-width="effectivePanelWidth"
        :has-panel="hasPanel"
        :is-resizing="isResizing"
        @drag-start="$emit('dragStart', $event)"
      >
        <template #rail>
          <slot name="rail"></slot>
        </template>
        <template #panel>
          <slot name="panel"></slot>
        </template>
      </AppShellLeftMenu>
      <button
        v-if="hasPanel"
        class="app-shell__menu-toggle"
        :class="{ 'is-collapsed': isCollapsed }"
        aria-label="メニューの開閉"
        :aria-expanded="!isCollapsed"
        v-tooltip.top="isCollapsed ? 'メニューを開く' : 'メニューを折りたたむ'"
        @click="$emit('toggle')"
      >
        <DgrIcon
          size="small"
          :name="`chevron-${isCollapsed ? 'right' : 'left'}`"
        />
      </button>
    </div>
    <div class="app-shell__content" ref="contentRef">
      <slot name="content"></slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.app-shell {
  --layout-content-width: 948px;
  --layout-header-height: #{$header-height};
  --layout-header-offset: 0px;
  --layout-sticky-top: var(--layout-header-offset);
  // タブ付きヘッダーの下でstickyにする要素の位置（ヘッダーあり・なしのsticky-top + タブ41px）
  --layout-sticky-top-under-tabs: calc(var(--layout-sticky-top) + 41px);
  --layout-left-menu-collapsed-width: #{$left-section-width};
  --layout-left-menu-panel-width: 0px;
  --layout-left-menu-width: calc(
    var(--layout-left-menu-collapsed-width) +
      var(--layout-left-menu-panel-width)
  );

  height: 100dvh;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-rows: 100dvh;
  overflow: hidden;
}

.app-shell__header {
  position: fixed;
  inset: 0 0 auto 0;
  height: var(--layout-header-height);
  z-index: var(--z-header);
  transform: translateY(var(--layout-header-offset));
  transition: transform 160ms ease;
}

.app-shell__menu {
  position: relative;
  height: 100dvh;
  padding-top: calc(var(--layout-header-height) + var(--layout-header-offset));
  box-sizing: border-box;
  background: $color-white;
  z-index: var(--z-left-menu);
  transition: padding-top 160ms ease;
}

.app-shell__menu-toggle {
  position: absolute;
  bottom: 16px;
  left: calc(var(--layout-left-menu-width) - 48px);
  z-index: var(--z-left-menu);
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: $color-white;
  cursor: pointer;
  transition: left 160ms ease;

  &.is-collapsed {
    left: calc(var(--layout-left-menu-width) - 16px);
    box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);
  }

  &:hover {
    background-color: $color-gray200;
  }
}

.app-shell__content {
  height: 100dvh;
  padding-top: var(--layout-header-height);
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: auto;
}

.app-shell.is-resizing {
  .app-shell__header,
  .app-shell__menu {
    transition: none;
  }

  user-select: none;
}
</style>
